import { Api } from "@core/api/api";
import { pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { MediaDto, mediaFromDto } from "./dtos";
import { AssetType, Media, MediaUploadInfos } from "./media";
import { OpenGraphData } from "./openGraphData";

const MEDIA_URL = "/assets";

export class MediaApi {
	constructor(private readonly api: Api) {}

	async fetch(id: string): Promise<Media> {
		const result = await this.api.get<MediaDto>(`${MEDIA_URL}/${id}`);
		return mediaFromDto(result.data);
	}

	/** TODO if necessary use base64 image */
	// async fetchBase64(id: string): Promise<string> {
	// 	const result = await this.api.get<string>(`${MEDIA_URL}/${id}/image`);
	// 	return result.data;
	// }

	async list(pageOptions: PaginationOptions, query?: string, type?: AssetType): Promise<Page<Media>> {
		const result = await this.api.get<Page<MediaDto>>(
			`${MEDIA_URL}?${pageParams(pageOptions)}&filter=${query}${type ? `&type=${type}` : ""}`
		);
		return { ...result.data, content: result.data.content.map((asset) => mediaFromDto(asset)) };
	}

	delete(id: string): Promise<void> {
		return this.api.delete(`${MEDIA_URL}/${id}`);
	}

	async edit(id: string, displayName: string): Promise<Media> {
		const result = await this.api.put<MediaDto>(`${MEDIA_URL}/${id}`, { displayName, visible: true });
		return mediaFromDto(result.data);
	}

	async uploadMedia(file: File, onUpload?: (progress: number) => void): Promise<Media> {
		const formData = new FormData();
		formData.append("file", file);

		const result = await this.api.post<MediaDto>(MEDIA_URL, formData, {
			onUploadProgress: (e) => {
				if (e.total) {
					onUpload?.(e.loaded / e.total);
				}
			},
		});
		return mediaFromDto(result.data);
	}

	async getOpenGraphData(url: string): Promise<OpenGraphData> {
		const result = await this.api.post<OpenGraphData>(`${MEDIA_URL}/opengraph`, { url });
		return result.data;
	}

	async startUpload(size: number, partSize: number): Promise<MediaUploadInfos> {
		const result = await this.api.post<MediaUploadInfos>(`${MEDIA_URL}/initiate-upload`, { size, partSize });
		return result.data;
	}

	async uploadChunk(chunk: Blob, url: string, onUpload?: (progress: number) => void): Promise<string> {
		const result = await this.api.put(url, chunk, {
			onUploadProgress: (e) => {
				if (e.total) {
					onUpload?.(e.loaded / e.total);
				}
			},
		});

		return result.headers["etag"];
	}

	async finishUpload(
		key: string,
		uploadId: string,
		partEtags: string[],
		mimeType: string,
		displayName: string
	): Promise<Media> {
		const result = await this.api.post<MediaDto>(`${MEDIA_URL}/finalize-upload`, {
			key,
			uploadId,
			mimeType,
			partEtags,
			displayName,
		});
		return mediaFromDto(result.data);
	}
}
